import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, Info, Trash2, X } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
// ---------------------------------------------------------------------------
// Context + Provider
// ---------------------------------------------------------------------------
const ConfirmContext = /*#__PURE__*/ createContext(null);
/**
 * useConfirm — async confirmation dialog that replaces native `confirm()`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete 5 sections?',
 *     description: 'This will also delete all subsections. This cannot be undone.',
 *     variant: 'danger',
 *     confirmLabel: 'Delete',
 *   });
 *   if (ok) { ... }
 */ export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error('useConfirm must be used inside <ConfirmDialogProvider>');
    return ctx.confirm;
}
/**
 * Wrap your app (or a subtree) with this provider to enable `useConfirm()`.
 */ export function ConfirmDialogProvider({ children }) {
    const [state, setState] = useState(null);
    const resolveRef = useRef(null);
    const confirm = useCallback((opts)=>{
        return new Promise((resolve)=>{
            resolveRef.current = resolve;
            setState({
                ...opts,
                open: true
            });
        });
    }, []);
    const handleClose = (result)=>{
        resolveRef.current?.(result);
        resolveRef.current = null;
        setState(null);
    };
    return /*#__PURE__*/ _jsxs(ConfirmContext.Provider, {
        value: {
            confirm
        },
        children: [
            children,
            state?.open && /*#__PURE__*/ _jsx(ConfirmDialogOverlay, {
                title: state.title,
                description: state.description,
                variant: state.variant ?? 'info',
                confirmLabel: state.confirmLabel ?? 'Confirm',
                cancelLabel: state.cancelLabel ?? 'Cancel',
                onConfirm: ()=>handleClose(true),
                onCancel: ()=>handleClose(false)
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Dialog UI
// ---------------------------------------------------------------------------
function ConfirmDialogOverlay({ title, description, variant, confirmLabel, cancelLabel, onConfirm, onCancel }) {
    const isDanger = variant === 'danger';
    const IconComponent = isDanger ? AlertTriangle : Info;
    const iconBg = isDanger ? 'bg-rose-100' : 'bg-blue-100';
    const iconColor = isDanger ? 'text-rose-600' : 'text-blue-600';
    const confirmBg = isDanger ? 'bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-300' : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-300';
    return /*#__PURE__*/ _jsxs("div", {
        className: "fixed inset-0 z-50 flex items-center justify-center p-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "absolute inset-0 bg-black/40 backdrop-blur-[2px]",
                onClick: onCancel,
                role: "presentation"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-0 shadow-2xl animate-in fade-in zoom-in-95 duration-150",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: onCancel,
                        className: "absolute right-3 top-3 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "px-6 pt-6 pb-4",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start gap-3",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: `flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBg}`,
                                    children: /*#__PURE__*/ _jsx(IconComponent, {
                                        className: `h-5 w-5 ${iconColor}`
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0 flex-1 pt-0.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-[15px] font-semibold text-slate-900",
                                            children: title
                                        }),
                                        description && /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1.5 text-sm leading-relaxed text-slate-600",
                                            children: description
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-3",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: onCancel,
                                className: "rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300",
                                children: cancelLabel
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: onConfirm,
                                className: `inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:outline-none focus-visible:ring-2 ${confirmBg}`,
                                children: [
                                    isDanger && /*#__PURE__*/ _jsx(Trash2, {
                                        className: "h-3.5 w-3.5"
                                    }),
                                    confirmLabel
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
